import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  TablePagination,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import {
  setTargetBounds,
  setEditingEntity,
  setDrawingMode,
  setOwnersLayerKey,
  setFieldsLayerKey,
  setCurrentField,
} from "../../reducers/MapSlice";
import {
  updateOwner,
  getOwnerDashboard,
  getOwnerBoundaries,
  deleteOwner,
} from "../../api/FlaskAPI";
import { areaUnitShort, formatArea } from "../../utils/areaUnits";
import { useTranslation } from "react-i18next";
import {
  MAX_OWNER_NAME_LENGTH,
  MAX_PHONE_LENGTH,
} from "../../constants/fieldConstants";
import { alertKeys, fieldKeys, ownerKeys } from "../../api/queryKeys";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import "./DashboardPage.css";
import { ComplianceBadge } from "../MapPopup";
import { TableFilters, NameWithId } from "./TableCells";
import SummaryCards, { sumArea } from "./SummaryCards";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";

const columns = [
  { key: "name", label: "Owner", sortable: true, type: "text" },
  { key: "phone", label: "Phone", sortable: true, type: "text" },
  { key: "area", label: "Area", sortable: true, type: "number" },
  { key: "compliant", label: "Compliance", sortable: true, type: "boolean" },
];

const FILTER_OPTIONS = [
  {
    key: "compliant",
    Icon: GppMaybeOutlinedIcon,
  },
];

const OwnersPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  // Pagination state (page is 0-based for TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Alert filter dropdown state
  const [selectedAlertFilters, setSelectedAlertFilters] = useState([]);

  // Dialog state
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const loadErrorMessage = t("ownersPage.loadFailed");
  const areaUnit = useSelector((state) => state.settings.areaUnit);
  const queryClient = useQueryClient();

  // Debounce the search box so each keystroke doesn't hit the backend
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const params = useMemo(() => {
    const params = { page: page + 1, per_page: rowsPerPage };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (sortConfig.key) {
      params.sort_by = sortConfig.key;
      params.sort_dir = sortConfig.direction;
    }
    if (selectedAlertFilters.length > 0) {
      params.filter = selectedAlertFilters.join(",");
    }
    params.area_unit = areaUnit;
    return params;
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    sortConfig,
    selectedAlertFilters,
    areaUnit,
  ]);

  // keepPreviousData leaves the current rows on screen while the next page,
  // search or sort loads, instead of blanking the table to a spinner.
  const {
    data,
    isPending: loading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ownerKeys.list(params),
    queryFn: ({ signal }) =>
      getOwnerDashboard(params, signal).then((res) => res.data),
    placeholderData: keepPreviousData,
    meta: { errorMessage: loadErrorMessage },
  });
  const owners = data?.items || [];
  const totalCount = data?.total || 0;

  // count the whole project, not the current page or search.
  const allParams = { area_unit: areaUnit };
  const { data: allData } = useQuery({
    queryKey: ownerKeys.list(allParams),
    queryFn: ({ signal }) =>
      getOwnerDashboard(allParams, signal).then((res) => res.data),
  });
  const allOwners = allData?.items;
  const notCompliant = allOwners?.filter((o) => !o.compliant).length;
  const summary = [
    {
      label: t("dashboard.nav.owners"),
      value: allOwners?.length,
      Icon: PersonOutlinedIcon,
    },
    {
      label: t("dashboard.cards.totalArea"),
      value:
        allOwners &&
        formatArea(sumArea(allOwners), areaUnit, { withUnit: false }),
      note: areaUnitShort(areaUnit),
      Icon: SquareFootOutlinedIcon,
    },
    {
      label: t("dashboard.cards.notCompliant"),
      value: notCompliant,
      Icon: GppMaybeOutlinedIcon,
      tone: notCompliant ? "error" : "neutral",
    },
  ];
  const error = isError ? loadErrorMessage : null;

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
    setPage(0);
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };

  // Alert filter dropdown handlers

  const handleRowClick = (owner) => {
    setSelectedOwner(owner);
    setEditForm({ ...owner });
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOwner(null);
    setIsEditing(false);
    setEditForm({});
  };

  const handleShowArea = async () => {
    if (selectedOwner) {
      try {
        const response = await getOwnerBoundaries(selectedOwner.id);
        const bounds = response.data;
        const leafletBounds = [
          [bounds[0][0], bounds[0][1]],
          [bounds[1][0], bounds[1][1]],
        ];
        dispatch(setTargetBounds(leafletBounds));
        // An owner is not a field: leaving the previous field selected shows a
        // panel that has nothing to do with where the map just went.
        dispatch(setCurrentField(null));
        setDialogOpen(false);
        navigate("/");
      } catch (err) {
        console.error("Error fetching owner boundaries:", err);
        toast.error(t("ownersPage.toasts.boundsFailed"));
      }
    }
  };

  const handleEditBoundary = async () => {
    if (!selectedOwner) return;
    if (!editForm || Object.keys(editForm).length === 0) return;
    try {
      const response = await getOwnerBoundaries(selectedOwner.id);
      const bounds = response.data;
      const leafletBounds = [
        [bounds[0][0], bounds[0][1]],
        [bounds[1][0], bounds[1][1]],
      ];
      dispatch(setTargetBounds(leafletBounds));
      dispatch(
        setEditingEntity({
          id: selectedOwner.id,
          properties: editForm,
        }),
      );
      dispatch(setDrawingMode("owner"));
      dispatch(setCurrentField(null));
      setDialogOpen(false);
      navigate("/");
    } catch (err) {
      console.error("Error fetching owner boundaries:", err);
      toast.error(t("ownersPage.toasts.boundsEditFailed"));
    }
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOwner) return;
    setDeleting(true);
    try {
      const response = await deleteOwner(selectedOwner.id);
      if (response.status === 200 || response.status === 204) {
        toast.success(t("ownersPage.toasts.deleted"));
        // The owner's fields and their alerts are deleted with it.
        queryClient.invalidateQueries({ queryKey: fieldKeys.all });
        queryClient.invalidateQueries({ queryKey: alertKeys.all });
        // Waiting for the refetch keeps the deleted row from flashing back.
        await queryClient.invalidateQueries({ queryKey: ownerKeys.all });
        setConfirmDeleteOpen(false);
        setDialogOpen(false);
        setSelectedOwner(null);

        dispatch(setFieldsLayerKey());
        dispatch(setOwnersLayerKey());
      } else {
        const msg =
          response.data?.message || t("ownersPage.toasts.deleteFailed");
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || t("ownersPage.toasts.deleteFailed");
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedOwner) return;
    if (!editForm.name) {
      toast.error(t("area.toasts.ownerName"));
      return;
    }
    if (editForm.name.length > MAX_OWNER_NAME_LENGTH) {
      toast.error(
        t("area.toasts.ownerNameMax", { max: MAX_OWNER_NAME_LENGTH }),
      );
      return;
    }
    if (!editForm.phone) {
      toast.error(t("area.toasts.phone"));
      return;
    }
    if (editForm.phone.length > MAX_PHONE_LENGTH) {
      toast.error(t("area.toasts.phoneMax", { max: MAX_PHONE_LENGTH }));
      return;
    }

    setSaving(true);
    try {
      const response = await updateOwner({
        id: selectedOwner.id,
        coordinates: null,
        name: editForm.name,
        phone: editForm.phone,
        compliant: editForm.compliant,
        area_unit: areaUnit,
      });
      if (response.status === 200 || response.status === 204) {
        toast.success(t("area.toasts.ownerUpdated"));
        // Field rows show owner details too.
        queryClient.invalidateQueries({ queryKey: fieldKeys.all });
        await queryClient.invalidateQueries({ queryKey: ownerKeys.all });
        // Prefer the server's row; it's omitted on the "no fields" reply.
        setSelectedOwner((prev) => ({
          ...prev,
          ...editForm,
          ...(response.data?.updated_owner ?? {}),
        }));
        dispatch(setFieldsLayerKey());
        dispatch(setOwnersLayerKey());
        setIsEditing(false);
      } else {
        toast.error(t("ownersPage.toasts.updateFailed"));
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || t("ownersPage.toasts.updateFailed");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const renderCellContent = (owner, column) => {
    const value = owner[column.key];

    if (column.key === "name") {
      return <NameWithId name={owner.name} id={owner.id} />;
    }
    if (column.key === "compliant") {
      return <ComplianceBadge compliant={value} />;
    }

    if (column.key === "area") {
      return formatArea(value, areaUnit, { withUnit: false });
    }

    return value || "-";
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h1>{t("ownersPage.title")}</h1>
          <p>{t("ownersPage.subtitle")}</p>
        </div>
      </div>

      <SummaryCards cards={summary} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          placeholder={t("dashboard.searchOwners")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{
            minWidth: { xs: "100%", sm: 300 },
            backgroundColor: "white",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
        <TableFilters
          options={FILTER_OPTIONS}
          selected={selectedAlertFilters}
          onChange={(next) => {
            setSelectedAlertFilters(next);
            setPage(0);
          }}
        />
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: "#6b7280" }}>
        {t("ownersPage.showing", { shown: owners.length, total: totalCount })}
        {selectedAlertFilters.length > 0 &&
          ` · ${FILTER_OPTIONS.filter((o) =>
            selectedAlertFilters.includes(o.key),
          )
            .map((o) => t(`dashboard.filters.phrases.${o.key}`))
            .join(` ${t("dashboard.filters.or")} `)}`}
      </Typography>

      {/* Reserves the bar's height so the table doesn't jump when it appears. */}
      <Box sx={{ height: 4, mb: 1 }}>
        {isFetching && !loading && <LinearProgress />}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && owners.length === 0 && (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
            {t("ownersPage.noneFound")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            {searchQuery || selectedAlertFilters.length > 0
              ? t("dashboard.tryAdjusting")
              : t("ownersPage.noData")}
          </Typography>
        </Paper>
      )}

      {!loading && !error && owners.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "8px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
              overflowX: "auto",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        cursor: column.sortable ? "pointer" : "default",
                        userSelect: "none",
                        "&:hover": column.sortable
                          ? { backgroundColor: "#e5e7eb" }
                          : {},
                      }}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {column.key === "area"
                          ? t("dashboard.areaHeader", {
                              unit: areaUnitShort(areaUnit),
                            })
                          : t(`ownersPage.columns.${column.key}`)}
                        {column.sortable && getSortIcon(column.key)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {owners.map((owner, index) => (
                  <TableRow
                    key={owner.id || index}
                    onClick={() => handleRowClick(owner)}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "white" : "#fafafa",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#e8f4f8" },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align || "left"}
                        sx={{
                          whiteSpace: "nowrap",
                          color: "#4b5563",
                        }}
                      >
                        {renderCellContent(owner, column)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {/* Owner Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            pr: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("ownersPage.dialogTitle")}
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            aria-label={t("common.close")}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOwner && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isEditing ? (
                <>
                  <TextField
                    label={t("area.ownerName")}
                    value={editForm.name || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    fullWidth
                    size="small"
                    required
                    inputProps={{ maxLength: MAX_OWNER_NAME_LENGTH }}
                    helperText={`${(editForm.name || "").length}/${MAX_OWNER_NAME_LENGTH}`}
                  />
                  <TextField
                    label={t("terms.phone")}
                    value={editForm.phone || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    required
                    inputProps={{ maxLength: MAX_PHONE_LENGTH }}
                    helperText={`${(editForm.phone || "").length}/${MAX_PHONE_LENGTH}`}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      checked={!!editForm.compliant}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          compliant: e.target.checked,
                        }))
                      }
                    />
                    <Typography variant="body2">
                      {t("terms.compliant")}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    <strong>{t("ownersPage.details.id")}</strong>{" "}
                    {selectedOwner.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("ownersPage.details.name")}</strong>{" "}
                    {selectedOwner.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("ownersPage.details.phone")}</strong>{" "}
                    {selectedOwner.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("ownersPage.details.area")}</strong>{" "}
                    {formatArea(selectedOwner.area, areaUnit)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("ownersPage.details.compliant")}</strong>{" "}
                    {selectedOwner.compliant === true
                      ? t("ownersPage.yes")
                      : t("ownersPage.no")}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          {isEditing ? (
            <>
              <Button
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({});
                }}
                color="inherit"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleEditBoundary}
                variant="outlined"
                color="primary"
              >
                {t("dashboard.editBoundary")}
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("dashboard.save")
                )}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} color="inherit">
                {t("common.close")}
              </Button>
              <Button
                onClick={handleShowArea}
                variant="contained"
                color="primary"
              >
                {t("dashboard.showArea")}
              </Button>
              <Button
                onClick={() => setIsEditing(true)}
                variant="outlined"
                startIcon={<EditIcon />}
              >
                {t("dashboard.edit")}
              </Button>
              <Button
                onClick={handleDeleteClick}
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
              >
                {t("common.delete")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        kind="owner"
        elementId={selectedOwner?.name}
        handleConfirmDelete={handleConfirmDelete}
        isDialogOpen={confirmDeleteOpen}
        handleOnClose={handleCloseConfirmDelete}
        deleting={deleting}
      />
    </div>
  );
};

export default OwnersPage;
